import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  colors,
  Hidden,
  IconButton,
  SvgIcon,
  Toolbar,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import React, { lazy, Suspense, useEffect } from "react";
import { Menu as MenuIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import Logo from "src/components/Logo";
import { THEMES } from "src/constants";
import { useSelector } from "src/store";
import Account from "./Account";
import Search from "./Search";
import Settings from "./Settings";
// import Help from "./Help";
var configSite = window["configSite"];

const Help = lazy(() => import("./Help"));
const useStyles = makeStyles((theme) => ({
  root: {
    zIndex: theme.zIndex.drawer + 100,
    background: theme.palette.background.default,
    color: colors.common.white,
    boxShadow: theme.shadows[6],
    ...(theme.name === THEMES.LIGHT
      ? {
          // boxShadow: '',
          background: theme.palette.background.default,
          color: colors.common.white,
        }
      : {}),
    ...(theme.name === THEMES.ONE_DARK
      ? {
          backgroundColor: theme.palette.background.default,
        }
      : {}),
  },
  toolbar: {
    minHeight: 64,
    boxShadow: theme.shadows[6],
  },
  adminMode: {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: colors.common.white,
  },
  logo: {
    // maxWidth: "8rem",
    maxHeight: "3rem",
    width: "auto",
  },
  unpaid: {
    backgroundColor: `${theme.palette.error.main} !important`,
    color: colors.common.white,
    minHeight: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  warning: {
    backgroundColor: `${theme.palette.warning.main} !important`,
    color: colors.common.white,
    minHeight: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  suspendedBanner: {
    background: `linear-gradient(90deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 55%, #b71c1c 100%)`,
    color: colors.common.white,
    minHeight: 72,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
  },
  suspendedContent: {
    width: "100%",
    maxWidth: 1180,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    textAlign: "left",
  },
  suspendedTitle: {
    fontWeight: 700,
    lineHeight: 1.2,
  },
  suspendedMessage: {
    opacity: 0.95,
    lineHeight: 1.35,
  },
}));

const TopBar = ({ className, onMobileNavOpen, ...rest }) => {
  const { customerName } = useParams();
  const settings = useSelector((state) => state.settings);
  const { currentCustomer } = useSelector((state) => state.customers);
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  let appBarClasses = clsx(classes.root, className);
  const { user, isAuthenticated } = useSelector((state) => state.users);
  const [state, setState] = React.useState({
    notShowDemoAlert: localStorage.getItem("notShowDemoAlert"),
    showUpdateIdentities: localStorage.getItem("showUpdateIdentities"),
    enabledUpdateIdentities: false,
  });

  const resetShowUpdateIdentities = () => {
    localStorage.removeItem("showUpdateIdentities");
    setState((prevState) => ({
      ...prevState,
      showUpdateIdentities: null,
    }));
  };

  const checkUpdatePath = () => {
    if (location.pathname.startsWith("/app/account/selectIdentity/")) {
      return true;
    }
    if (location.pathname.startsWith("/app/account/identityProfile/")) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    // if two hours have passed since the user last updated their identities
    if (
      state.showUpdateIdentities &&
      Date.now() - state.showUpdateIdentities > 1000 * 60 * 60 * 2
    ) {
      resetShowUpdateIdentities();
    }
  }, [state.showUpdateIdentities]);

  useEffect(() => {
    if (
      currentCustomer &&
      user?.licenses?.length &&
      ["sancarlos", "guacara", "ciudad"].includes(currentCustomer?.shortname)
    ) {
      setState((prevState) => ({
        ...prevState,
        enabledUpdateIdentities: true,
      }));
    }
  }, [user, currentCustomer]);

  if (settings.adminMode) {
    appBarClasses = clsx(appBarClasses, classes.adminMode);
  }

  return (
    <AppBar>
      <Toolbar className={classes.toolbar}>
        <Hidden lgUp>
          <IconButton color="inherit" onClick={onMobileNavOpen} size="large">
            <SvgIcon fontSize="small">
              <MenuIcon />
            </SvgIcon>
          </IconButton>
        </Hidden>

        <RouterLink
          to={
            currentCustomer && !window.location.hostname.endsWith("arkadu.com")
              ? `/${currentCustomer.shortname}`
              : isAuthenticated
              ? window["APP_HOME"]
              : `/`
          }
        >
          <Logo className={classes.logo} />
        </RouterLink>
        {/* show date and time in format Lunes, 24-01-2025 10:15AM */}
        <Typography
          variant="body2"
          color="inherit"
          sx={{
            textTransform: "capitalize",
            marginLeft: 2,
            // on small hide the date
            display: { xs: "none", sm: "block" },
          }}
        >
          {/* date should be in customer.timezone ex America/Caracas */}
          {format(
            new Date(
              new Date().toLocaleString("en-US", {
                timeZone: currentCustomer?.timezone || "America/Caracas",
              }),
            ),
            "EEEE, dd-MM-yyyy hh:mm a",
            {
              locale: es,
            },
          )}
        </Typography>
        <Box ml={2} flexGrow={1} />
        {/* {!checkUpdatePath() &&
          state.enabledUpdateIdentities &&
          !state.showUpdateIdentities && (
            <>
              <Typography variant="h6" color="inherit">
                {t("Es necesario actualizar su información.")}
              </Typography>{" "}
              <Button
                variant="contained"
                color="warning"
                href={"/app/account/selectIdentity/"}
                sx={{
                  ml: 2,
                }}
              >
                {t("Actualizar información")}
              </Button>
              <Box flexGrow={1} />
            </>
          )} */}

        {/* if host != arkadu */}

        {/* <AndroidApp /> */}
        <Suspense fallback={<CircularProgress />}>
          <Help />
        </Suspense>
        {/* {settings.adminMode && (
          <Button
            id="ps-support-button"
            variant="contained"
            color="secondary"
            sx={{ ml: 2 }}
            data-priority-support="button"
            startIcon={<SupportAgentRounded />}
          >
            {t("Soporte")}
          </Button>
        )} */}
        <Search />
        {/* <Contacts /> */}
        {/* <Notifications /> */}

        <Settings />

        <Box ml={2}>
          <Account />
        </Box>
      </Toolbar>

      {(!state.notShowDemoAlert ||
        Date.now() - state.notShowDemoAlert > 1000 * 60 * 60 * 72) &&
        configSite?.siteDemo && (
          <Toolbar className={classes.unpaid}>
            <Typography variant="h6" color="inherit">
              {t(
                "Está en modo demo, todos los datos se cargan en el servidor de pruebas y serán eliminados en 72 horas",
              )}
            </Typography>
            <Box flexGrow={1} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                localStorage.setItem("notShowDemoAlert", Date.now());
                setState({ ...state, notShowDemoAlert: Date.now() });
              }}
            >
              {t("No mostrar durante 72 horas")}
            </Button>
          </Toolbar>
        )}
      {settings.adminMode && window["SUSPENDED_ACCOUNT"] && (
        <Toolbar className={classes.suspendedBanner}>
          <Box className={classes.suspendedContent}>
            <Typography
              variant="subtitle1"
              color="inherit"
              className={classes.suspendedTitle}
            >
              {t("Atención: su cuenta presenta pagos pendientes.")}
            </Typography>
            <Typography
              variant="body2"
              color="inherit"
              className={classes.suspendedMessage}
            >
              {t(
                "Regularice su saldo para mantener el acceso administrativo y evitar la suspensión automática en 2 días.",
              )}
            </Typography>
          </Box>
        </Toolbar>
      )}
      {/* on small screens show date in format Lunes, 24-01-2025 10:15AM under logo */}
      <Hidden smUp>
        <Toolbar
          className={classes.toolbar}
          sx={{
            justifyContent: "center",
            padding: 0,
            height: "0.9rem",
            minHeight: "0.9rem !important",
            boxShadow: "none !important",
          }}
        >
          <Typography
            variant="body2"
            color="inherit"
            sx={{
              textTransform: "capitalize",
              marginLeft: 2,
              paddingTop: 0.5,
              display: { xs: "block", sm: "none" },
              fontSize: "0.8rem",
            }}
          >
            {format(
              new Date(
                new Date().toLocaleString("en-US", {
                  timeZone: currentCustomer?.timezone || "America/Caracas",
                }),
              ),
              "EEEE, dd-MM-yyyy hh:mm a",
              {
                locale: es,
              },
            )}
          </Typography>
        </Toolbar>
      </Hidden>
    </AppBar>
  );
};

TopBar.defaultProps = {
  onMobileNavOpen: () => {},
};

export default TopBar;
